import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Edit, History, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { usePrompt } from '@/api/queries/prompts';
import { useTopics } from '@/api/queries/promptTopics';
import { EditPromptDialog } from '@/features/prompts/EditPromptDialog';
import { VersionHistoryDialog } from '@/features/prompts/VersionHistoryDialog';
import { PromptStatusEnum } from '@/types/prompt';

export default function PromptDetailPage() {
  const { t } = useTranslation('prompts');
  const { promptId } = useParams<{ promptId: string }>();
  const navigate = useNavigate();
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(false);

  const { data, isLoading, error } = usePrompt(promptId || '', !!promptId);
  const { data: topicsData } = useTopics();

  const getTopicName = (topicId: string) => {
    return topicsData?.topics?.find(topic => topic._id === topicId)?.name || '';
  };

  const handleCopyTemplate = async () => {
    if (data?.prompt.prompt_template) {
      try {
        await navigator.clipboard.writeText(data.prompt.prompt_template);
        setCopiedTemplate(true);
        setTimeout(() => setCopiedTemplate(false), 2000);
      } catch (err) {
        console.error(t('messages.failedToCopyTemplate'), err);
      }
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case PromptStatusEnum.ACTIVE.valueOf():
        return 'default';
      case PromptStatusEnum.DRAFT.valueOf():
        return 'secondary';
      case PromptStatusEnum.ARCHIVED.valueOf():
        return 'outline';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">{t('messages.loadingPrompt')}</div>
      </div>
    );
  }

  if (error || !data?.prompt) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600 py-8">
          {t('messages.errorLoadingPrompt', {
            message: error instanceof Error ? error.message : t('messages.promptNotFound'),
          })}
        </div>
      </div>
    );
  }

  const prompt = data.prompt;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/admin/prompts')}
        className="flex items-center gap-2 !px-0"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('navigation.backToPrompts')}
      </Button>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-gray-900">{prompt.name}</h1>
              <Badge variant={getStatusBadgeVariant(prompt.status)}>{prompt.status}</Badge>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {getTopicName(prompt.topic_id)}
              </Badge>
            </div>
            <p className="text-gray-600">{prompt.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowVersionHistory(true)}
            className="flex items-center gap-2"
          >
            <History className="h-4 w-4" />
            {t('navigation.versionHistoryButton')}
          </Button>
          <Button
            onClick={() => setEditingPrompt(true)}
            className="flex items-center gap-2 text-white"
          >
            <Edit className="h-4 w-4" />
            {t('navigation.editPromptButton')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Prompt Template */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('promptTemplate')}</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyTemplate}
                  className="flex items-center gap-2"
                >
                  {copiedTemplate ? (
                    <>
                      <Check className="h-4 w-4" />
                      {t('actions.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      {t('actions.copy')}
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={prompt.prompt_template}
                readOnly
                className="min-h-[50vh] font-mono text-sm bg-gray-50 resize-none"
              />
            </CardContent>
          </Card>

          {/* Keywords */}
          {prompt.query_keywords.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('queryKeywords')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {prompt.query_keywords.map((keyword, index) => (
                    <Badge key={index} variant="secondary">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Required Tools */}
          {prompt.required_tools.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('requiredTools')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {prompt.required_tools.map((tool, index) => (
                    <Badge key={index} variant="outline">
                      {tool}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('labels.basicInformation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('promptId')}:</span>
                <p className="text-sm text-gray-900 font-mono">{prompt.prompt_id}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">{t('status')}:</span>
                <p className="text-sm text-gray-900">{prompt.status}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">{t('topic')}:</span>
                <p className="text-sm text-gray-900">{getTopicName(prompt.topic_id)}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">{t('labels.created')}:</span>
                <p className="text-sm text-gray-900">
                  {new Date(prompt.metadata.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">{t('labels.updated')}:</span>
                <p className="text-sm text-gray-900">
                  {new Date(prompt.metadata.updated_at).toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">{t('labels.createdBy')}:</span>
                <p className="text-sm text-gray-900">{prompt.metadata.created_by}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      {editingPrompt && (
        <EditPromptDialog open={editingPrompt} onOpenChange={setEditingPrompt} prompt={prompt} />
      )}

      <VersionHistoryDialog
        open={showVersionHistory}
        onOpenChange={setShowVersionHistory}
        promptId={prompt.prompt_id}
      />
    </div>
  );
}
